import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import { Heading, Loader, TableContainer, TBody, TD, TH, TR } from '@tesla/design-system-react';
import { createQueryParamsFromObject, getIdFromSlug } from '../../utils/common';
import { ERROR_LOG_TYPES, logSentryError } from '../../utils/sentry';
import { Error, FooterCopy } from '../../components';
import { storeJob } from '../../actions';
import messages from './messages';
import Apply from './Apply';
import style from './style.module.scss';

const JobDetails = () => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const params = useParams();
  const { slug } = params;
  const jobId = getIdFromSlug(slug);
  const apiUrl = useSelector((state) => state.app.jobUrl);
  const jobs = useSelector((state) => state.jobs.data);
  const filters = useSelector((state) => state.main.filters);
  const location = useLocation();
  const dispatch = useDispatch();
  const store = useCallback((data) => dispatch(storeJob(data)), [dispatch]);

  const [loadingState, setLoadingState] = useState('loading');
  const [job, setJob] = useState({});

  let url = '/';
  if (filters) {
    url += `?${createQueryParamsFromObject(filters)}`;
  }

  const setMetaTags = async (currentJob) => {
    const currentJobUrl = currentJob.url || '/careers/search';
    const twitter = document.querySelector('meta[name="twitter:url"]');

    if (typeof twitter !== 'undefined') {
      twitter.setAttribute('content', currentJobUrl);
    }

    const og = document.querySelector('meta[property="og:url"]');
    if (typeof og !== 'undefined') {
      og.setAttribute('content', currentJobUrl);
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (typeof canonical !== 'undefined') {
      canonical.setAttribute('href', currentJobUrl);
    }
  };

  const loadJob = async (id) => {
    try {
      const response = await axios.get(`${apiUrl}/${jobId}`);
      if (response && response.status === 200) {
        const { data } = response;
        store(data);
        setJob(data);
        setLoadingState('done');

        await setMetaTags(data);
      }
    } catch (e) {
      logSentryError('CAREERS_RETRIEVE_JOB_DETAILS_FAILURE', ERROR_LOG_TYPES.ERROR, {
        jobId: id,
      });
      setLoadingState('error');
    }
  };

  useEffect(async () => {
    if (!apiUrl || !jobId) {
      setLoadingState('error');
      return;
    }

    if (jobs[jobId]) {
      setJob(jobs[jobId]);
      setLoadingState('done');
      return;
    }

    // Fetch data from the back-end.
    await loadJob(jobId);
  }, []);

  switch (loadingState) {
    case 'loading':
      return <Loader show="true" />;
    case 'error':
      return <Error />;
    case 'done':
    default:
      return (
        <div className="careers-width-constraint">
          <div className="tds-content_container tds-content_container--large">
            <div className="tds-flex">
              <Link to={url} className="tds-link">
                {formatMessage(messages.GoToSearch)}
              </Link>

              {job?.jobVideo && (
                // eslint-disable-next-line jsx-a11y/media-has-caption
                <video className={style.jobVideo} controls>
                  <source src={job.jobVideo} type="video/mp4" />
                </video>
              )}
            </div>

            <Heading is="h1" looksLike="h1-alt">
              {job.title}
            </Heading>

            <TableContainer className="tds-o-details-table">
              <TBody>
                <TR className={style.tdsOTableRow}>
                  <TH scope="row">{formatMessage(messages.JobCategory)}</TH>
                  <TD>{job.department}</TD>
                </TR>
                <TR className={style.tdsOTableRow}>
                  <TH scope="row">{formatMessage(messages.Location)}</TH>
                  <TD>{job.location}</TD>
                </TR>
                <TR className={style.tdsOTableRow}>
                  <TH scope="row">{formatMessage(messages.Id)}</TH>
                  <TD>{job.id}</TD>
                </TR>
                <TR className={style.tdsOTableRow}>
                  <TH scope="row">{formatMessage(messages.JobType)}</TH>
                  <TD>{job.timeType}</TD>
                </TR>
              </TBody>
            </TableContainer>

            <Apply
              applyUrl={job.applyUrl}
              jobId={job.id}
              query={location.search}
              templateId={job.applicationType}
              department={job.department}
            />

            {!isEmpty(job.jobDescription) && (
              <div className={style.description}>
                <strong>{formatMessage(messages.JobDescription)}</strong>
                <div
                  className={style.descriptionItem}
                  /* eslint-disable-next-line react/no-danger */
                  dangerouslySetInnerHTML={{ __html: job.jobDescription }}
                />

                {!isEmpty(job.jobResponsibilities) && (
                  <>
                    <strong>{formatMessage(messages.JobResponsibilities)}</strong>
                    <div
                      className={style.descriptionItem}
                      /* eslint-disable-next-line react/no-danger */
                      dangerouslySetInnerHTML={{ __html: job.jobResponsibilities }}
                    />
                  </>
                )}

                {!isEmpty(job.jobRequirements) && (
                  <>
                    <strong>{formatMessage(messages.JobRequirements)}</strong>
                    <div
                      /* eslint-disable-next-line react/no-danger */
                      dangerouslySetInnerHTML={{ __html: job.jobRequirements }}
                    />
                  </>
                )}
              </div>
            )}

            {isEmpty(job.jobDescription) && (
              <div
                className={style.description}
                /* eslint-disable-next-line react/no-danger */
                dangerouslySetInnerHTML={{ __html: job.description }}
              />
            )}

            {!isEmpty(job.jobCompensationAndBenefits) && (
              <div className={style.compensationAndBenefits}>
                <strong>{formatMessage(messages.JobCompensationAndBenefits)}</strong>
                <div
                  /* eslint-disable-next-line react/no-danger */
                  dangerouslySetInnerHTML={{ __html: job.jobCompensationAndBenefits }}
                />
              </div>
            )}

            <FooterCopy />
          </div>
        </div>
      );
  }
};

export default JobDetails;
